// S5 · Шаг 3 — RUSSIAN for the plant map (`onboarding.plant.*`).
//
// Register notes:
//  · «Зафиксировать» for commit — it contrasts with «черновик» the way the
//    English contrasts commit with draft, and it carries the right finality for
//    a screen whose whole promise is "nothing is created until you press this".
//  · «Архивировать», not «удалить»: the operation archives, and saying delete
//    would misdescribe what the tenancy module does.
//  · Entity nouns interpolate ({{facility}}, {{department}}, {{workCenter}},
//    {{machine}}) and arrive in the nominative singular. Every sentence here is
//    shaped so that reads correctly — «Добавить: {{facility}}» rather than an
//    accusative that Russian would demand and this file cannot produce.

import type { Translated } from "../../../../i18n-shape.js";
import type { plantMapEn } from "./plant-map.i18n.js";

export const plantMapRu: Translated<typeof plantMapEn> = {
  intro: "Разложите предприятие деревом, затем зафиксируйте его.",

  treeHeading: "Дерево предприятия",
  addFacility: "Добавить: {{facility}}",
  removeFacility: "Убрать: {{facility}}",
  addDepartment: "Добавить: {{department}}",
  removeDepartment: "Убрать: {{department}}",
  addWorkCenter: "Добавить: {{workCenter}}",
  removeWorkCenter: "Убрать: {{workCenter}}",
  moveUp: "Выше",
  moveDown: "Ниже",
  removeConfirm: "Убрать это из черновика? До фиксации это убирается только из вашего черновика.",

  facilityLabel: "{{facility}} {{number}}",
  departmentsHeading: "Список: {{department}}",
  workCentersHeading: "Список: {{workCenter}}",
  storageHeading: "Места хранения",
  addStorage: "Добавить место хранения",
  removeStorage: "Убрать место хранения",

  codeLabel: "Код",
  nameLabel: "Название",
  timezoneLabel: "Часовой пояс",
  addressLabel: "Адрес",
  addressHint: "Оставьте пустым, чтобы сохранить уже записанный адрес.",
  templateLabel: "Шаблон",
  templateNone: "Без шаблона",
  templateNoPack: "Установите отраслевой пакет, чтобы появились шаблоны.",
  machineCountLabel: "Количество ({{machine}})",
  machineCountHint:
    "При первой фиксации создаст коды {{example}} для оборудования ({{machine}}) — и только пока их нет у этого рабочего центра ({{workCenter}}).",
  parentLabel: "Внутри",
  parentNone: "Верхний уровень",
  storageHint:
    "Склады, стеллажи и зоны. Начальные остатки и этикетки мест хранения сопоставляются по этим кодам.",
  codeHint:
    "Короткий, постоянный, уникальный. Фиксация сопоставляет по коду — переименование кода создаёт новую запись.",

  emptyTitle: "Составьте карту предприятия",
  emptyBody:
    "Добавьте первую площадку ({{facility}}), затем её подразделения ({{department}}) и рабочие центры ({{workCenter}}).",
  emptyCta: "Добавить первую площадку ({{facility}})",
  seedTitle: "У вас уже есть зафиксированное предприятие",
  seedBody:
    "Загрузите зафиксированное дерево в черновик, чтобы править то, что на заводе есть на самом деле.",
  seedCta: "Загрузить зафиксированное дерево",
  seedNote:
    "Места хранения и количество оборудования ({{machine}}) в это дерево пока не загружаются, поэтому в нём только то, что можно прочитать сегодня.",

  problemsTitle: "Исправьте это до фиксации",
  warningsTitle: "Стоит проверить",
  problem: {
    facility_code_required: "{{path}} — площадке ({{facility}}) нужен код.",
    facility_name_required: "{{path}} — площадке ({{facility}}) нужно название.",
    facility_timezone_required: "{{path}} — площадке ({{facility}}) нужен часовой пояс.",
    department_code_required: "{{path}} — подразделению ({{department}}) нужен код.",
    department_name_required: "{{path}} — подразделению ({{department}}) нужно название.",
    work_center_code_required: "{{path}} — рабочему центру ({{workCenter}}) нужен код.",
    work_center_name_required: "{{path}} — рабочему центру ({{workCenter}}) нужно название.",
    storage_code_required: "{{path}} — месту хранения нужен код.",
    storage_name_required: "{{path}} — месту хранения нужно название.",
    duplicate_facility_code: "{{path}} — этот код площадки ({{facility}}) использован дважды.",
    duplicate_department_code:
      "{{path}} — этот код подразделения ({{department}}) использован дважды в пределах площадки ({{facility}}).",
    duplicate_work_center_code:
      "{{path}} — этот код рабочего центра ({{workCenter}}) использован дважды в пределах площадки ({{facility}}). Коды уникальны в пределах одной площадки ({{facility}}), по всем подразделениям ({{department}}).",
    duplicate_storage_code:
      "{{path}} — этот код места хранения использован дважды в пределах площадки ({{facility}}).",
    unknown_storage_parent:
      "{{path}} — ни у одного места хранения на этой площадке ({{facility}}) нет такого родительского кода.",
    work_center_moved:
      "{{path}} — такой рабочий центр ({{workCenter}}) уже есть в другом подразделении ({{department}}). Фиксация обновит его название, но не перенесёт его.",
  },

  // ---- §3.7 редактор структуры (UX-1) --------------------------------------
  draftChip: "черновик",
  toggleNode: "Развернуть или свернуть: {{node}}",
  wcMachinesMeta: "{{count}} — {{machine}}",
  ghostAddWorkCenter: "+ Добавить {{workCenter}} в {{department}}",
  ghostAddDepartment: "+ Добавить {{department}} в {{facility}}",
  ghostAddStorage: "+ Добавить место хранения в {{facility}}",
  storageMeta: "хранение",
  removeFromDraft: "Убрать {{node}} из черновика…",
  // МНОЖЕСТВЕННОЕ ЧИСЛО. i18next сначала ищет `key_<категория>`, а затем
  // откатывается к голому ключу. Русские категории few/many здесь намеренно не
  // заводятся: голые формы построены так, что число стоит ПОСЛЕ существительного
  // («Узлов: 3»), и потому грамматичны при любом счёте; `_one` даёт точную
  // единственную форму там, где она звучит лучше.
  nodeCount: "Узлов: {{count}} · в черновике: {{draft}}",
  nodeCount_one: "Узлов: {{count}} · в черновике: {{draft}}",
  propsEmptyTitle: "Свойства",
  propsEmptyBody: "Выберите узел в дереве, чтобы редактировать его здесь.",

  // ---- живое предложение фиксации (§3.7) -----------------------------------
  sentence: {
    create: "Фиксация создаст {{parts}}.",
    facilities: "{{count}} — {{facility}} ({{names}})",
    departments: "{{count}} — {{department}} ({{names}})",
    workCenters: "{{count}} — {{workCenter}} ({{names}})",
    storage: "{{count}} — места хранения",
    storage_one: "{{count}} — место хранения",
    restore: "Восстанавливается из архива, а не создаётся заново, узлов: {{count}} — {{names}}.",
    restore_one:
      "Восстанавливается из архива, а не создаётся заново, узлов: {{count}} — {{names}}.",
    edited: "Обновляется существующих узлов: {{count}} — {{names}}.",
    edited_one: "Обновляется существующих узлов: {{count}} — {{names}}.",
    matched: "Существующих узлов, сопоставленных по коду и оставленных как есть: {{count}}.",
    matched_one: "Существующий узел сопоставлен по коду и оставлен как есть: {{count}}.",
    onlyDeletions:
      "Фиксация ничего не создаст. Она только архивирует отсутствующие в черновике узлы ({{count}}) — и лишь после вашего подтверждения ниже.",
    onlyDeletions_one:
      "Фиксация ничего не создаст. Она только архивирует один отсутствующий в черновике узел — и лишь после вашего подтверждения ниже.",
    comma: ", ",
    and: " и ",
  },
  commitValidated: "Проверено · конфликтов нет",
  discardDraft: "Отменить черновик",

  // ---- состояние 4 · ОДНА запись об успехе (§3.8) --------------------------
  committedTitle: "Карта предприятия зафиксирована.",
  committedNodes: "Зафиксировано новых узлов: {{count}} —",
  committedNodes_one: "Зафиксирован новый узел: {{count}} —",
  committedCount: "Зафиксировано новых узлов: {{count}}.",
  committedCount_one: "Зафиксирован новый узел: {{count}}.",
  restoredNodes: "Восстановлено из архива:",
  setupDelta: "Готовность +{{delta}} → {{score}}",
  setupScore: "Готовность {{score}}",
  dismissBanner: "Скрыть",
  continueNext: "Далее: {{step}}",
  resultPart: "{{count}} × {{entity}}",
  resultCreatedLine: "Создано: {{parts}}.",
  resultUpdatedLine: "Обновлено: {{updated}} · архивировано: {{deleted}}.",

  // ---- состояние 5 · последующая работа, свёрнуто (телефон) ----------------
  followUpTitle: "Привязать шаги и пригласить людей",
  followUpHint: "Работа по зафиксированному предприятию",

  diffHeading: "Что сделает фиксация",
  diffCreate: "Создать",
  diffAdopt: "Сопоставить с существующим",
  diffFacilities: "Список: {{facility}}",
  diffDepartments: "Список: {{department}}",
  diffWorkCenters: "Список: {{workCenter}}",
  diffStorage: "Места хранения",
  diffMachines: "Список: {{machine}}",
  diffNothing: "Фиксировать пока нечего.",
  diffUpdateNote:
    "Уже существующая запись обновляется, только если изменились её название или часовой пояс.",

  absentTitle: "Зафиксированные узлы, которых нет в черновике",
  absentBody:
    "Они существуют на вашем заводе, но в этот черновик не попали. По умолчанию фиксация их не трогает.",
  absentKind: {
    facility: "{{facility}}",
    department: "{{department}}",
    work_center: "{{workCenter}}",
  },
  confirmDeletionsLabel: "При фиксации архивировать отсутствующие узлы ({{count}})",
  confirmDeletionsLabel_one: "При фиксации архивировать отсутствующий узел",
  confirmDeletionsHint:
    "Архивирование — это настоящее изменение. Узел, у которого ещё есть дочерние элементы или участники, будет отклонён, а не заархивирован.",
  deleteConfirmTitle: "Заархивировать зафиксированные узлы ({{count}})?",
  deleteConfirmTitle_one: "Заархивировать этот зафиксированный узел?",
  deleteConfirmBody:
    "При сохранении они будут отправлены в архив. Если хотя бы одна запись ещё используется, ничего не архивируется.",
  deleteConfirmCta: "Да, зафиксировать и заархивировать",
  cancel: "Отмена",

  existingLoading: "Читаем зафиксированное предприятие…",
  existingForbiddenTitle: "Зафиксированное предприятие вам не видно",
  existingForbiddenBody:
    "Ваша роль не может читать организационное дерево, поэтому экран не покажет, что уже существует, — и ничего не предложит архивировать.",
  existingErrorTitle: "Не удалось прочитать зафиксированное предприятие",
  existingErrorBody: "Сводка изменений и список на удаление недоступны, пока это не загрузится.",

  commitHeading: "Фиксация карты предприятия",
  commitCta: "Зафиксировать карту предприятия",
  commitWorking: "Фиксируется…",
  commitBlocked: "Исправьте проблемы выше, прежде чем фиксировать.",
  commitReadOnly: "У вас нет прав фиксировать карту предприятия.",
  commitFrozen: "Ваш завод запущен. Карта предприятия сохраняется как запись о настройке.",
  commitSaveFirst:
    "Фиксация отправляет черновик в том виде, как он на экране. Сохраните его тоже, чтобы он пережил обновление страницы.",

  resultCreated: "Создано",
  resultUpdated: "Обновлено",
  resultDeleted: "Заархивировано",
  resultNoChange: "Ничего не изменилось — черновик уже совпадает с зафиксированным предприятием.",
  resultFacilities: "Список: {{facility}}",
  resultDepartments: "Список: {{department}}",
  resultWorkCenters: "Список: {{workCenter}}",
  resultStorage: "Места хранения",
  resultMachines: "Список: {{machine}}",

  pendingEntity: {
    storage_locations: "Места хранения",
    machines: "Список: {{machine}}",
  },
  pendingLine: "Записано, но ещё не создано — {{entity}}: {{count}}, ожидают модуль {{module}}.",

  duplicateTitle: "Повторяющийся код в черновике",
  duplicateBody:
    "У двух записей один и тот же код. Коды должны быть уникальны: {{facility}} — в пределах всей компании, {{department}} и {{workCenter}} — в пределах одной площадки ({{facility}}).",
  refusedTitle: "Часть удалений отклонена",
  refusedBody:
    "Ничего не заархивировано. На каждый узел ниже ещё есть ссылки — уберите их или верните узел в черновик.",
  refusedReasonLabel: "Указанная причина",
  commitFailedTitle: "Карта предприятия не зафиксирована",
  commitFailedDetail: "Ничего не изменилось. Попробуйте ещё раз.",

  removeConfirmTitle: "Убрать {{node}}?",
  removeConfirmBody:
    "{{node}} убирается из вашего черновика вместе с {{summary}}. Уже зафиксированное этим не удаляется.",
  removeConfirmCta: "Убрать",
  removeConfirmUndoHint: "Сразу после этого можно вернуть кнопкой «Отменить».",
  removeCount: {
    departments: "{{count}} — {{department}}",
    workCenters: "{{count}} — {{workCenter}}",
    storage: "{{count}} — места хранения",
    storage_one: "{{count}} — место хранения",
    machines: "{{count}} — {{machine}}",
  },
  removedNotice: "{{node}} убрано из черновика.",
  undoRemove: "Отменить",

  facilitySummary: "{{departments}} — {{department}} · {{workCenters}} — {{workCenter}}",
  departmentSummary: "{{workCenters}} — {{workCenter}} · {{machines}} — {{machine}}",
  bulkAddLabel: "Добавить сразу несколько ({{workCenter}})",
  bulkAddCta: "Добавить {{count}}",

  diffDeferredHeading: "Записано, но ещё не создано",
  diffDeferredEntry: "{{entity}}: {{count}} — ожидает модуль {{module}}",
  machineCountDeferred: "Вводится вручную.",
  refusedReasonGeneric:
    "Этот узел ещё где-то используется, поэтому не заархивирован. Ничего не изменилось.",

  bindings: {
    heading: "Привяжите рабочие центры",
    blurb:
      "Привяжите каждую операцию пакета к рабочему центру ({{workCenter}}). Это нужно для запуска.",
    facilityLabel: "{{facility}}",
    loading: "Загружаются привязки…",
    errorTitle: "Не удалось загрузить привязки",
    errorBody: "Привязки получить не удалось. Попробуйте ещё раз.",
    noPackTitle: "Сначала выберите отраслевой пакет",
    noPackBody:
      "Привязки соединяют операции пакета с вашими рабочими центрами, поэтому пакет идёт первым.",
    commitFirstTitle: "Сначала зафиксируйте карту предприятия",
    commitFirstBody:
      "Привязки указывают на реальные площадки ({{facility}}), поэтому зафиксируйте карту выше — и они появятся здесь.",
    noTemplatesTitle: "В этом пакете нет операций",
    noTemplatesBody: "Привязывать нечего. Эта проверка пройдёт сама собой.",
    noWorkCentersTitle: "Рабочих центров ({{workCenter}}) пока нет",
    noWorkCentersBody:
      "Добавьте и зафиксируйте хотя бы один рабочий центр ({{workCenter}}) на этой площадке, затем вернитесь и привяжите операции.",
    unbound: "Не привязано",
    unboundBadge: "Не привязано",
    boundBadge: "Привязано",
    summary: "Привязано шагов: {{bound}} из {{total}}",
    unboundWarning: "Непривязанных операций: {{count}}. До их привязки запуск заблокирован.",
    unboundWarning_one: "Непривязанная операция: {{count}}. До её привязки запуск заблокирован.",
    save: "Сохранить привязки",
    saving: "Сохраняется…",
    saveFailedTitle: "Не удалось сохранить привязки",
    saveFailedBody: "Ничего не изменилось. Попробуйте ещё раз.",
    replaceNote:
      "Сохранение заменяет весь набор для этой площадки — всё, что осталось непривязанным, останется непривязанным.",
    readOnly: "Ваша роль не может менять привязки.",
  },

  people: {
    heading: "Сотрудники",
    blurb: "Пригласите администраторов, планировщиков и рабочих цеха вашего завода.",
    bulkHint: "Список уже есть?",
    bulkCta: "Загрузить сотрудников из таблицы",
    notPermitted: "Ваша роль не может приглашать сотрудников. Обратитесь к администратору группы.",
    emailLabel: "Электронная почта",
    roleLabel: "Роль",
    rolePlaceholder: "Выберите роль…",
    scopeLabel: "Доступ",
    scopeGroup: "Вся группа",
    scopeFacility: "Одна площадка ({{facility}})",
    facilityLabel: "{{facility}}",
    facilityPlaceholder: "Выберите одну…",
    commitFirst: "Сначала зафиксируйте карту предприятия, чтобы создать площадку ({{facility}}).",
    invite: "Отправить приглашение",
    inviting: "Отправляется…",
    inviteFailedTitle: "Не удалось отправить приглашение",
    inviteFailedBody: "Никто не приглашён. Проверьте адрес и попробуйте ещё раз.",
    loading: "Загружаются приглашения…",
    errorTitle: "Не удалось загрузить приглашения",
    errorBody: "Список приглашений получить не удалось. Попробуйте ещё раз.",
    emptyTitle: "Незакрытых приглашений нет",
    emptyBody: "Пока никого не пригласили. Начните с приглашения администраторов площадок.",
    colEmail: "Электронная почта",
    colScope: "Доступ",
    colExpires: "Действует до",
    colActions: "Действия",
    scope: {
      group: "Вся группа",
      facility: "Одна площадка",
      department: "Одно подразделение",
    },
    resend: "Отправить повторно",
    resendCapped: "Это приглашение отправляли повторно максимальное число раз.",
    revoke: "Отозвать",
    revokeConfirmTitle: "Отозвать это приглашение?",
    revokeConfirmBody:
      "{{email}} не сможет воспользоваться отправленной ссылкой. Пригласить заново можно в любой момент.",
  },
};
